import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { Avatar } from '../../components/Avatar';
import { ConfirmDialog } from '../../components/Modal';
import { SettingsHeader } from '../../components/SettingsLayout';
import { errorMessage } from '../../lib/api';
import { membersApi } from '../../lib/endpoints';
import { qk } from '../../lib/queryClient';
import { displayName } from '../../store/selectors';
import { toast } from '../../store/ui';
import type { BanDto } from '@lagerfeuer/shared';

/** Tab „Banns": Liste mit Grund, Suche und Aufheben. */
export function BansTab({ serverId }: { serverId: string }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: qk.bans(serverId), queryFn: () => membersApi.bans(serverId, { limit: 1000 }) });
  const [filter, setFilter] = useState('');
  const [confirm, setConfirm] = useState<BanDto | null>(null);
  const [busy, setBusy] = useState(false);
  const f = filter.trim().toLowerCase();
  const bans = (q.data ?? []).filter((b) => !f || b.user.username.toLowerCase().includes(f) || (b.user.displayName ?? '').toLowerCase().includes(f) || b.user.id === f);

  const unban = async (b: BanDto) => {
    setBusy(true);
    try {
      await membersApi.unban(serverId, b.user.id);
      qc.setQueryData<BanDto[]>(qk.bans(serverId), (list) => list?.filter((x) => x.user.id !== b.user.id));
      toast.success(`Bann für ${displayName(b.user)} aufgehoben`);
    } catch (e) {
      toast.error('Aufheben fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Banns">Gebannte Nutzer können dem Server nicht wieder beitreten, bis der Bann aufgehoben wird.</SettingsHeader>
      <div className="search-input">
        <Search size={16} aria-hidden="true" />
        <input className="input" placeholder="Banns durchsuchen (Name oder ID)" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Banns durchsuchen" data-testid="ban-search" />
      </div>
      {q.isPending ? (
        <div className="empty">
          <span className="spinner" aria-label="Lädt" />
        </div>
      ) : q.isError ? (
        <p className="error-text" role="alert">
          {errorMessage(q.error)}
        </p>
      ) : bans.length === 0 ? (
        <p className="empty" data-testid="ban-empty">
          {f ? 'Keine Treffer.' : 'Niemand ist gebannt. Friedlich hier am Feuer.'}
        </p>
      ) : (
        <ul className="settings-list" data-testid="ban-list">
          {bans.map((b) => (
            <li key={b.user.id} className="settings-row" data-testid="ban-row" data-user-id={b.user.id}>
              <Avatar user={b.user} size={36} />
              <div className="member-settings-main">
                <strong className="truncate">{displayName(b.user)}</strong>
                <span className="muted small">
                  @{b.user.username} · seit {new Date(b.createdAt).toLocaleDateString('de-DE')}
                </span>
                <span className="small">{b.reason ? `Grund: ${b.reason}` : <span className="faint">Kein Grund angegeben</span>}</span>
              </div>
              <button className="btn btn-sm" onClick={() => setConfirm(b)} data-testid="ban-revoke">
                Bann aufheben
              </button>
            </li>
          ))}
        </ul>
      )}
      {confirm && (
        <ConfirmDialog
          title={`Bann für ${displayName(confirm.user)} aufheben?`}
          message="Der Nutzer kann danach mit einer Einladung wieder beitreten."
          confirmLabel="Bann aufheben"
          busy={busy}
          onConfirm={() => void unban(confirm)}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
